import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import './JsonEditor.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const JsonEditor = ({
  jsonData,
  onChange,
  isNew,
  isAdmin,
  transactionId,
  documentId,
  fromCustomerProfiling = false,
  onEditChange,
}) => {
  const [jsonObj, setJsonObj] = useState(null);
  const [addFieldPaths, setAddFieldPaths] = useState(new Set());
  const [selectedPath, setSelectedPath] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [confidenceMap, setConfidenceMap] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const [inputStates, setInputStates] = useState({});
  const isInitialMount = useRef(true);
  const lastDataKey = useRef({ transactionId, documentId });
  const pollIntervalRef = useRef(null);
  const skipNextHistorySave = useRef(false);
  const activeInputRef = useRef(null);

  // Detect current route
  const location = useLocation();
  const isFromCustomerProfilingPage = 
    fromCustomerProfiling || 
    location.pathname.includes('/customer-profiling') || 
    location.pathname.includes('/customer-documents');

  // Debug log to verify route and condition
  useEffect(() => {
    console.log('JsonEditor Route Debug:', {
      pathname: location.pathname,
      isFromCustomerProfilingPage,
      fromCustomerProfiling,
    });
  }, [location.pathname, fromCustomerProfiling, isFromCustomerProfilingPage]);

  // Disable editing when fromCustomerProfiling is true
  const isEditable = (isNew || isAdmin) && !fromCustomerProfiling;

  useEffect(() => {
    console.log('JsonEditor props:', { transactionId, documentId, isPolling });
    if (jsonData) {
      const dataKey = { transactionId, documentId };
      const isNewData =
        isInitialMount.current ||
        dataKey.transactionId !== lastDataKey.current.transactionId ||
        dataKey.documentId !== lastDataKey.current.documentId;

      if (isNewData) {
        const initialState = JSON.parse(JSON.stringify(jsonData));
        setJsonObj(initialState);
        setHistory([initialState]);
        setHistoryIndex(0);
        setConfidenceMap({});
        setIsPolling(false);
        setInputStates({});
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        lastDataKey.current = dataKey;
        isInitialMount.current = false;
      }
    }
  }, [jsonData, transactionId, documentId]);

  const saveToHistory = (currentObj) => {
    if (skipNextHistorySave.current) {
      skipNextHistorySave.current = false;
      return;
    }
    
    const newHistory = history.slice(0, historyIndex + 1);
    const newState = JSON.parse(JSON.stringify(currentObj));
    newHistory.push(newState);
    if (newHistory.length > 50) {
      newHistory.shift();
    }
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex <= 0) {
      return;
    }
    
    skipNextHistorySave.current = true;
    const prevIndex = historyIndex - 1;
    const prevState = JSON.parse(JSON.stringify(history[prevIndex]));
    
    setJsonObj(prevState);
    setHistoryIndex(prevIndex);
    onChange(prevState);
    setSelectedPath(null);
    setInputStates({});
    onEditChange(false);
  };

  const startSecondaryOcr = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No authentication token found.');
      console.log('Starting secondary OCR with:', { transactionId, documentId });
      const response = await axios.post(
        `${API_BASE_URL}/documents/start-secondary-ocr`,
        {
          transaction_set_id: transactionId,
          document_id: documentId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.task_id;
    } catch (error) {
      console.error('Error starting secondary OCR:', error);
      toast.error('Failed to Start Secondary OCR: ' + (error.message || 'Unknown error'), {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: 'toast-error',
      });
      throw error;
    }
  };

  const getSecondaryOcrStatus = async (taskId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No authentication token found.');
      const response = await axios.get(
        `${API_BASE_URL}/documents/secondary-ocr-status/${taskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting secondary OCR status:', error);
      toast.error('Failed to Get Secondary OCR Status: ' + (error.message || 'Unknown error'), {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: 'toast-error',
      });
      throw error;
    }
  };

  const getConfidenceMap = (result, path = '') => {
    let map = {};
    
    if (result && typeof result === 'object') {
      if ('value' in result && '_confidence' in result) {
        map[path] = result._confidence;
      } else if (Array.isArray(result)) {
        result.forEach((item, index) => {
          const childPath = path ? `${path}[${index}]` : `[${index}]`;
          const childMap = getConfidenceMap(item, childPath);
          map = { ...map, ...childMap };
        });
      } else {
        for (const key in result) {
          if (result.hasOwnProperty(key)) {
            const childPath = path ? `${path}.${key}` : key;
            const childMap = getConfidenceMap(result[key], childPath);
            map = { ...map, ...childMap };
          }
        }
      }
    }
    
    return map;
  };

  const handleCheckConfidence = async () => {
    if (isPolling) {
      toast.info('A secondary OCR task is already in progress.', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    try {
      setIsPolling(true);
      const taskId = await startSecondaryOcr();
      const startTime = Date.now();
      const maxPollDuration = 30000;
      pollIntervalRef.current = setInterval(async () => {
        if (Date.now() - startTime > maxPollDuration) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
          toast.error('Secondary OCR timed out after 30 seconds.', {
            position: 'bottom-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: 'toast-error',
          });
          return;
        }
        const statusData = await getSecondaryOcrStatus(taskId);
        if (statusData.status === 'completed' || statusData.status === 'success') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
          const confidenceMap = getConfidenceMap(statusData.result);
          console.log('Confidence Map:', confidenceMap);
          setConfidenceMap(confidenceMap);
          toast.success('Secondary OCR Completed - Confidence levels have been updated.', {
            position: 'bottom-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: 'toast-success',
          });
        } else if (statusData.status === 'failed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
          toast.error('Secondary OCR Failed: ' + (statusData.error || 'Unknown error'), {
            position: 'bottom-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            className: 'toast-error',
          });
        }
      }, 2000);
    } catch (error) {
      setIsPolling(false);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  };

  const escapeHtml = (unsafe) => {
    if (typeof unsafe !== "string") return unsafe;
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const getDisplayKey = (path) => {
    if (path === "") return "";
    const parts = path.split(".");
    const lastPart = parts[parts.length - 1];
    if (lastPart.includes("[")) {
      return lastPart.match(/\[\d+\]$/)?.[0] || lastPart;
    }
    return lastPart;
  };

  const formatValueForDisplay = (value) => {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "boolean") return value.toString();
    if (typeof value === "number") {
      return value.toString();
    }
    if (typeof value === "string") return value;
    return JSON.stringify(value);
  };

  const parsePath = (path) => {
    if (!path) return [];
    const parts = [];
    const segments = path.split(".");
    segments.forEach((segment) => {
      if (segment.includes("[")) {
        const keyPart = segment.split("[")[0];
        if (keyPart) parts.push(keyPart);
        const indices = segment.match(/\[(\d+)\]/g);
        if (indices) {
          indices.forEach((idx) => parts.push(parseInt(idx.slice(1, -1))));
        }
      } else {
        parts.push(segment);
      }
    });
    return parts;
  };

  const parseValue = (valueStr) => {
    if (valueStr === "null") return null;
    if (valueStr === "undefined") return undefined;
    if (valueStr === "true") return true;
    if (valueStr === "false") return false;
    if (valueStr === "{}" || valueStr === "object") return {};
    if (valueStr === "[]" || valueStr === "array") return [];
    if (valueStr === "") return "";

    const numberRegex = /^-?\d*\.?\d+$/;
    if (numberRegex.test(valueStr)) {
      if (valueStr.endsWith('.') && !valueStr.includes('..')) {
        return valueStr;
      }
      
      const num = parseFloat(valueStr);
      if (!isNaN(num) && isFinite(num)) {
        return num;
      }
    }

    if (/^-?\d+\.$/.test(valueStr)) {
      return valueStr;
    }

    try {
      return JSON.parse(valueStr);
    } catch (e) {
      if (valueStr.startsWith('"') && valueStr.endsWith('"')) {
        return valueStr.slice(1, -1);
      }
      return valueStr;
    }
  };

  const setValue = (obj, path, value) => {
    const parts = parsePath(path);
    const newObj = JSON.parse(JSON.stringify(obj));
    let current = newObj;
    for (let i = 0; i < parts.length - 1; i++) {
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
    return newObj;
  };

  const deleteValue = (obj, path) => {
    const parts = parsePath(path);
    const newObj = JSON.parse(JSON.stringify(obj));
    let current = newObj;
    for (let i = 0; i < parts.length - 1; i++) {
      current = current[parts[i]];
    }
    const lastPart = parts[parts.length - 1];
    if (typeof lastPart === "number") {
      current.splice(lastPart, 1);
    } else {
      delete current[lastPart];
    }
    return newObj;
  };

  const changeKey = (obj, path, newKey) => {
    const parts = parsePath(path);
    const oldKey = parts.pop();
    const parentPath = parts.join(".");
    const newObj = JSON.parse(JSON.stringify(obj));
    let parent = parentPath
      ? parsePath(parentPath).reduce((o, p) => o[p], newObj)
      : newObj;
    if (newKey in parent) return null;
    parent[newKey] = parent[oldKey];
    delete parent[oldKey];
    return newObj;
  };

  const addField = (obj, path, key, value) => {
    const newObj = JSON.parse(JSON.stringify(obj));
    const target = path
      ? parsePath(path).reduce((o, p) => o[p], newObj)
      : newObj;
    const isArray = Array.isArray(target);
    if (isArray) {
      target.push(value);
    } else {
      target[key] = value;
    }
    return newObj;
  };

  const handleKeyChange = (path, newKey) => {
    if (!isEditable) return;
    saveToHistory(jsonObj);
    const newObj = changeKey(jsonObj, path, newKey);
    if (newObj) {
      setJsonObj(newObj);
      onChange(newObj);
      onEditChange(true);
    } else {
      toast.warn('Duplicate Key - The key already exists in this object.', {
        position: 'bottom-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleValueChange = (path, newValue) => {
    if (!isEditable) return;
    
    console.log(`handleValueChange: path=${path}, newValue=${newValue}`);
    setInputStates(prev => {
      const newState = { ...prev, [path]: newValue };
      console.log('Updated inputStates:', newState);
      return newState;
    });

    saveToHistory(jsonObj);
    const parsedValue = parseValue(newValue);
    console.log(`Parsed value: ${parsedValue}, type: ${typeof parsedValue}`);
    const newObj = setValue(jsonObj, path, parsedValue);
    setJsonObj(newObj);
    onChange(newObj);
    onEditChange(true);
  };

  const handleDelete = (path) => {
    if (!isEditable) return;
    saveToHistory(jsonObj);
    const newObj = deleteValue(jsonObj, path);
    setJsonObj(newObj);
    onChange(newObj);
    if (selectedPath === path) {
      setSelectedPath(null);
    }
    setInputStates(prev => {
      const newState = { ...prev };
      delete newState[path];
      return newState;
    });
    onEditChange(true);
  };

  const handleAddField = (path) => {
    if (!isEditable) return;
    setAddFieldPaths((prev) => {
      const newSet = new Set(prev);
      newSet.add(path);
      return newSet;
    });
    onEditChange(true);
  };

  const handleSaveNew = (path, key, value) => {
    if (!isEditable) return;
    const target = path
      ? parsePath(path).reduce((o, p) => o[p], jsonObj)
      : jsonObj;
    const isArray = Array.isArray(target);

    if (isArray && key) {
      toast.warn('Invalid Input - Arrays do not require keys. Please leave the key field empty.', {
        position: 'bottom-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    if (!isArray && key in target) {
      toast.warn('Duplicate Key - The key already exists in this object.', {
        position: 'bottom-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }
    saveToHistory(jsonObj);
    const parsedValue = parseValue(value);
    const newObj = addField(jsonObj, path, key, parsedValue);
    setJsonObj(newObj);
    setAddFieldPaths((prev) => {
      const newSet = new Set(prev);
      newSet.delete(path);
      return newSet;
    });
    onChange(newObj);
    onEditChange(true);
  };

  const handleCancelNew = (path) => {
    if (!isEditable) return;
    setAddFieldPaths((prev) => {
      const newSet = new Set(prev);
      newSet.delete(path);
      return newSet;
    });
    onEditChange(false);
  };

  const handleRowSelect = (path) => {
    if (!isEditable) return;
    setSelectedPath(path === selectedPath ? null : path);
  };

  const getInputValue = (path, defaultValue) => {
    const inputValue = inputStates.hasOwnProperty(path) ? inputStates[path] : formatValueForDisplay(defaultValue);
    console.log(`getInputValue: path=${path}, inputValue=${inputValue}, defaultValue=${defaultValue}`);
    return escapeHtml(inputValue);
  };

  const isNumericalField = (value) => {
    return typeof value === 'number' || 
           (typeof value === 'string' && /^-?\d*\.?\d*$/.test(value) && value !== '');
  };

  const generateRows = (obj, path = "", level = 0) => {
    const displayKey = getDisplayKey(path);
    if (
      path !== "" &&
      (displayKey === "transaction_set_id" ||
        displayKey === "document_id" ||
        displayKey === "_id")
    ) {
      return [];
    }

    if (path === "" && typeof obj === "object" && obj !== null) {
      const rows = [];

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          const childPath = `[${index}]`;
          rows.push(...generateRows(item, childPath, level));
        });
      } else {
        for (let key in obj) {
          if (
            key === "transaction_set_id" ||
            key === "document_id" ||
            key === "_id"
          ) {
            continue;
          }
          const childPath = key;
          rows.push(...generateRows(obj[key], childPath, level));
        }
      }
      return rows;
    }

    const isArrayElement = path.endsWith("]");
    const isSelected = path === selectedPath;

    if (typeof obj !== "object" || obj === null) {
      const isNumber = isNumericalField(obj);
      return [
        <tr
          key={path}
          data-path={path}
          data-type="primitive"
          onClick={() => handleRowSelect(path)}
          className={isSelected ? 'selected-row' : ''}
        >
          <td style={{ paddingLeft: `${level * 20}px` }}>
            {isArrayElement ? (
              <span>{displayKey}</span>
            ) : isEditable ? (
              <input
                type="text"
                className="key-input"
                value={displayKey}
                data-path={path}
                onChange={(e) => handleKeyChange(path, e.target.value)}
              />
            ) : (
              <span>{displayKey}</span>
            )}
          </td>
          <td>
            {isEditable ? (
              <input
                type="text"
                value={getInputValue(path, obj)}
                data-path={path}
                className="value-input"
                onChange={(e) => handleValueChange(path, e.target.value)}
                onFocus={() => (activeInputRef.current = path)}
                placeholder={isNumber ? "Enter number (e.g., 123.45)" : "Enter value"}
              />
            ) : (
              <span>{escapeHtml(formatValueForDisplay(obj))}</span>
            )}
            {/* Hide confidence icons on Customer Profiling or ExistingCustomerDocs */}
            {confidenceMap[path] !== undefined && !isFromCustomerProfilingPage && (
              <span
                className={confidenceMap[path] ? "confidence-high" : "confidence-low"}
              >
                {confidenceMap[path] ? "Check" : "Warning"}
              </span>
            )}
          </td>
          {/* Actions column hidden */}
          {/* {isEditable && (
            <td>
              <button
                className="delete"
                data-path={path}
                onClick={() => handleDelete(path)}
              >
                −
              </button>
            </td>
          )} */}
        </tr>,
      ];
    }

    const isArray = Array.isArray(obj);
    const rows = [
      <tr
        key={path}
        data-path={path}
        data-type={isArray ? "array" : "object"}
        onClick={() => handleRowSelect(path)}
        className={isSelected ? 'selected-row' : ''}
      >
        <td style={{ paddingLeft: `${level * 20}px` }}>
          {isArray ? (
            <span>{displayKey}</span>
          ) : isEditable ? (
            <input
              type="text"
              className="key-input"
              value={displayKey}
              data-path={path}
              onChange={(e) => handleKeyChange(path, e.target.value)}
            />
          ) : (
            <span>{displayKey}</span>
          )}
        </td>
        <td></td>
        {/* Actions column hidden */}
        {/* {isEditable && (
          <td>
            <button
              className="add-field"
              data-path={path}
              onClick={() => handleAddField(path)}
            >
              +
            </button>
          </td>
        )} */}
      </tr>,
    ];

    if (isArray) {
      obj.forEach((item, index) => {
        const childPath = `${path}[${index}]`;
        rows.push(...generateRows(item, childPath, level + 1));
      });
    } else {
      for (let key in obj) {
        const childPath = `${path}.${key}`;
        rows.push(...generateRows(obj[key], childPath, level + 1));
      }
    }

    // Add field functionality hidden
    // if (isEditable && addFieldPaths.has(path)) {
    //   rows.push(
    //     <tr key={`${path}-add`} className="add-row">
    //       <td>
    //         {!isArray && (
    //           <input
    //             type="text"
    //             placeholder="Key"
    //             className="new-key"
    //             id={`new-key-${path}`}
    //           />
    //         )}
    //       </td>
    //       <td>
    //         <input
    //           type="text"
    //           placeholder="Value"
    //           className="new-value"
    //           id={`new-value-${path}`}
    //         />
    //       </td>
    //       <td>
    //         <button
    //           className="save-new"
    //           onClick={() => {
    //             const key = isArray
    //               ? null
    //               : document.getElementById(`new-key-${path}`).value;
    //             const value = document.getElementById(`new-value-${path}`).value;
    //             handleSaveNew(path, key, value);
    //           }}
    //           title="Save"
    //         >
    //           ✔
    //         </button>
    //         <button
    //           className="cancel-new"
    //           onClick={() => handleCancelNew(path)}
    //           title="Cancel"
    //         >
    //           ✖
    //         </button>
    //       </td>
    //     </tr>
    //   );
    // }

    return rows;
  };

  return (
    <div className="json-editor-container card">
      {isEditable && (
        <div className="json-editor-controls">
          <button
            className="undo-button btn-outlined"
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            title="Undo last change"
          >
            Undo
          </button>

          {/* Hide Confidence Button on Customer Profiling or ExistingCustomerDocs */}
          {!isFromCustomerProfilingPage && (
            <button
              className="newt-button newt-button-primary"
              onClick={handleCheckConfidence}
              disabled={isPolling || !transactionId || !documentId}
            >
              {isPolling ? 'Checking...' : 'Check Confidence'}
            </button>
          )}
        </div>
      )}
      <table id="json-table" className="table">
        <thead>
          <tr>
            {/* <th>Key</th>
            <th>Value</th> */}
            {/* Actions column header hidden */}
            {/* {isEditable && <th>Actions</th>} */}
          </tr>
        </thead>
        <tbody>{jsonObj ? generateRows(jsonObj) : null}</tbody>
      </table>
    </div>
  );
};

export default JsonEditor;